import { useMemo, useState } from 'react';
import { sortHand, type DurakLegal, type DurakView } from '@cardhub/engine';
import { PlayingCard } from '../cards/PlayingCard';
import { Hand } from '../cards/Hand';
import { Modal } from '../components/ui';
import { useT } from '../i18n';
import { useGame } from '../store/game';
import { MySeat, SeatView, useNarrow, useSpeech } from './common';

const SUIT_SYMBOL: Record<string, string> = { S: '♠', H: '♥', D: '♦', C: '♣' };

export function DurakTable({ scoresOpen, onCloseScores }: { scoresOpen: boolean; onCloseScores: () => void }) {
  const t = useT();
  const view = useGame((s) => s.view) as DurakView;
  const legal = useGame((s) => s.legal) as DurakLegal | null;
  const seats = useGame((s) => s.seats);
  const mySeat = useGame((s) => s.mySeat);
  const turn = useGame((s) => s.turn);
  const act = useGame((s) => s.act);
  const speech = useSpeech();
  const [selected, setSelected] = useState<string | null>(null);
  const hand = useMemo(() => sortHand(view.hand), [view.hand]);
  const attackable = new Set(legal?.attack ?? []);
  const defendable = new Set(legal?.defend ?? []);
  const name = (s: number) => seats[s]?.user?.username ?? `Seat ${s + 1}`;
  const myTurn = mySeat !== null && turn?.seats.includes(mySeat);
  const narrow = useNarrow();
  const cardSize = narrow ? 'sm' : 'md';
  const ended = view.phase === 'MATCH_END';
  const bout = view.phase === 'BOUT_END' ? view.lastBout : null;
  const defending = view.phase === 'DEFEND';
  const trumpSym = SUIT_SYMBOL[view.trump] ?? '';

  const onCard = (c: string) => {
    if (!myTurn) return;
    if (defending && defendable.has(c)) {
      setSelected(c);
      void act({ type: 'defend', card: c }).finally(() => setSelected(null));
    } else if (!defending && attackable.has(c)) {
      setSelected(c);
      void act({ type: 'attack', card: c }).finally(() => setSelected(null));
    }
  };
  const canPlay = (c: string) => (defending ? defendable.has(c) : attackable.has(c));

  const hint = !myTurn
    ? null
    : defending
      ? legal && legal.defend.length > 0
        ? t('du.defendHint', { trump: trumpSym })
        : t('du.cannotBeat')
      : view.table.length === 0
        ? t('du.leadHint')
        : view.taking
          ? t('du.pileOnHint')
          : t('du.throwHint');

  const boutNote = bout
    ? bout.outcome === 'taken'
      ? t('du.took', { name: name(bout.defender) })
      : t('du.beaten', { name: name(bout.defender) })
    : null;

  return (
    <>
      <div className="stage">
        <div className="felt" />
        {[0, 1, 2, 3].map((s) => {
          const place = view.out.indexOf(s);
          return (
            <SeatView
              key={s}
              seat={s}
              info={seats[s]}
              mySeat={mySeat}
              total={4}
              turn={turn}
              speech={speech[s]}
              folded={place >= 0}
              sub={<span className="dim num">{t('du.cards', { n: view.handCounts[s] ?? 0 })}</span>}
              tags={
                <>
                  {place >= 0 && <span className="tag seen">{t('du.out', { n: place + 1 })}</span>}
                  {!ended && place < 0 && s === view.attacker && <span className="tag">{t('du.attacker')}</span>}
                  {!ended && place < 0 && s === view.defender && <span className="tag blind">{t('du.defender')}</span>}
                  {ended && view.durak === s && <span className="tag blind">{t('du.durakTag')}</span>}
                </>
              }
            />
          );
        })}
        <div className="felt-center centered">
          <div className="center-note">
            {t('du.bout', { n: view.boutNo })} · {t('du.stock', { n: view.stockCount })} · {t('du.trump')} {trumpSym}
          </div>
          {view.trumpCard && !ended && (
            <div style={{ display: 'flex', justifyContent: 'center' }} aria-label={t('du.trumpCard')}>
              <PlayingCard id={view.trumpCard} size="sm" />
            </div>
          )}
          <div className="trick" aria-label="Cards on the table" style={{ display: 'flex', flexWrap: 'wrap', gap: narrow ? 8 : 14, justifyContent: 'center', position: 'relative', maxWidth: '92%' }}>
            {view.table.map((p) => (
              <div key={p.attack} style={{ position: 'relative', display: 'inline-block', paddingRight: narrow ? 14 : 20, paddingBottom: narrow ? 14 : 20 }}>
                <PlayingCard id={p.attack} size={cardSize} />
                {p.defense && (
                  <div style={{ position: 'absolute', left: narrow ? 14 : 20, top: narrow ? 14 : 20, transform: 'rotate(10deg)', zIndex: 2 }}>
                    <PlayingCard id={p.defense} size={cardSize} />
                  </div>
                )}
              </div>
            ))}
          </div>
          {boutNote && (
            <div className={`pill ${bout!.outcome === 'taken' ? 'danger' : 'accent'}`} role="status">
              {boutNote}
            </div>
          )}
          {!bout && !ended && view.taking && (
            <div className="pill danger" role="status">
              {t('du.taking', { name: name(view.defender) })}
            </div>
          )}
          {ended && (
            <div className="card glass" style={{ textAlign: 'center' }} role="status">
              <strong>{view.durak === null ? t('du.noDurak') : t('du.isDurak', { name: name(view.durak) })}</strong>
            </div>
          )}
        </div>
        <MySeat seat={mySeat !== null ? seats[mySeat] : undefined} sub={mySeat !== null ? <>{t('du.cards', { n: view.handCounts[mySeat] ?? 0 })}</> : null} />
      </div>
      <div className="table-bottom">
        {(hint || (myTurn && legal && (legal.canTake || legal.canPass))) && (
          <div className="action-bar" role="group" aria-label={t('sc.choose')} style={{ flexWrap: 'wrap', gap: 8 }}>
            {hint && <span style={{ fontSize: 'var(--fs-xs)', color: 'rgba(255,255,255,0.85)', flex: '1 1 100%' }}>{hint}</span>}
            {myTurn && legal?.canTake && (
              <button className="btn danger" style={{ flex: '1 1 40%' }} onClick={() => void act({ type: 'take' })}>
                {t('du.take')}
              </button>
            )}
            {myTurn && legal?.canPass && (
              <button className="btn primary" style={{ flex: '1 1 40%' }} onClick={() => void act({ type: 'pass' })}>
                {t('du.pass')}
              </button>
            )}
          </div>
        )}
        <Hand size={narrow ? 'md' : 'lg'} label="Your hand" maxStep={0.6}>
          {hand.map((c, i) => (
            <PlayingCard
              key={c}
              id={c}
              size={narrow ? 'md' : 'lg'}
              dealIndex={view.boutNo === 1 && view.table.length === 0 ? i : undefined}
              selected={selected === c}
              playable={myTurn && canPlay(c)}
              unplayable={myTurn && !canPlay(c)}
              onClick={() => onCard(c)}
            />
          ))}
        </Hand>
      </div>
      <Modal open={scoresOpen} onClose={onCloseScores} title={t('table.scores')}>
        <table className="scoreboard">
          <thead>
            <tr>
              <th>#</th>
              <th>{t('du.player')}</th>
              <th>{t('du.cardsLeft')}</th>
            </tr>
          </thead>
          <tbody>
            {[...view.out, ...[0, 1, 2, 3].filter((s) => !view.out.includes(s)).sort((a, b) => (view.handCounts[a] ?? 0) - (view.handCounts[b] ?? 0))].map((s, i) => (
              <tr key={s}>
                <td>{i + 1}</td>
                <td className="ellipsis" style={{ maxWidth: 140 }}>{name(s)}</td>
                <td className="num">{view.out.includes(s) ? t('du.outShort') : (view.handCounts[s] ?? 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="dim" style={{ fontSize: 'var(--fs-xs)' }}>{t('du.scoreHint')}</p>
      </Modal>
    </>
  );
}
